import { Component, computed, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { ENTITLEMENTS_MANAGE_ENTITLEMENT } from '@lb/api/user/access-entitlements';
import { ACCESS_PEOPLE_PATH } from '@lb/api/user/access-paths';
import { UserAccessService } from '@lb/api/user/services/user-access.service';
import { AccountStateService } from '@lb/state/account-state.service';
import { catchError, filter, map, of, switchMap, tap } from 'rxjs';
import { DetailsWidgetComponent } from '../details-widget/details-widget-component';
import { LoaderComponent } from '../loader/loader.component';

/** Administration's own gate, which the Access area sits under — without it the link would 403. */
const SETTINGS_MANAGE_ENTITLEMENT = 'Admin:Settings:Manage';

/**
 * The entitlements a person holds, for their Basic Information page, with a link to the Access area's
 * People tab where they are changed. Shown only in the admin app and only to holders of
 * Admin:Entitlements:Manage; for anyone else it renders nothing and never calls the API.
 */
@Component({
	selector: 'lb-user-entitlements-widget',
	standalone: true,
	imports: [RouterLink, DetailsWidgetComponent, LoaderComponent],
	templateUrl: './user-entitlements-widget.component.html',
	styleUrl: './user-entitlements-widget.component.scss'
})
export class UserEntitlementsWidgetComponent {
	private readonly _accountStateService = inject(AccountStateService);
	private readonly _userAccessService = inject(UserAccessService);

	userID = input.required<number>();

	canView = computed(() => this._accountStateService.isAdmin() && this._accountStateService.userEntitlements().includes(ENTITLEMENTS_MANAGE_ENTITLEMENT));
	canOpenAccess = computed(() => this.canView() && this._accountStateService.userEntitlements().includes(SETTINGS_MANAGE_ENTITLEMENT));
	peoplePath = ACCESS_PEOPLE_PATH;

	entitlementNames = signal<string[]>([]);
	/** True from the start: pages hand over 0 until their user has loaded, and "None" must not flash first. */
	loading = signal(true);
	failed = signal(false);

	/** 0 until there is someone to load and a viewer allowed to see them. */
	private readonly _loadUserID = computed(() => (this.canView() ? this.userID() : 0));

	constructor() {
		toObservable(this._loadUserID)
			.pipe(
				filter((userID) => userID > 0),
				tap(() => {
					this.loading.set(true);
					this.failed.set(false);
				}),
				switchMap((userID) =>
					this._userAccessService.getUserAccess(userID).pipe(
						map((access) => access.entitlements.map((entitlement) => entitlement.entitlementName)),
						catchError(() => {
							this.failed.set(true);
							return of([]);
						})
					)
				),
				takeUntilDestroyed()
			)
			.subscribe((names) => {
				this.entitlementNames.set(names);
				this.loading.set(false);
			});
	}
}
